import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { Card, Stack, Text, UnstyledButton } from '@mantine/core'
import { FloorPlanView, PhotoPreviewModal } from '@markku/components'
import {
  type AttachmentResponse,
  pinnedPlans,
  type VisitWithRelations,
} from '@markku/types'
import { useState } from 'react'

interface FloorPlanCardProps {
  visit: VisitWithRelations
}

const TYPE_LABELS: Record<string, MessageDescriptor> = {
  before: msg`Before`,
  after: msg`After`,
  defect: msg`Defect`,
  visit_video: msg`Video`,
}

/** Where on the floor plan the technician photographed what. */
export function FloorPlanCard({ visit }: FloorPlanCardProps) {
  const { t } = useLingui()
  const [shown, setShown] = useState<AttachmentResponse[]>()
  const plans = pinnedPlans(visit.attachments ?? [])

  if (!plans.length) return null

  const spotLabel = (spotAttachments: AttachmentResponse[]) => {
    const labels = spotAttachments.map((a) => {
      const type = TYPE_LABELS[String(a.metadata?.type)]
      return a.description || (type ? t(type) : '')
    })
    return [...new Set(labels)].filter(Boolean).join(' · ')
  }

  return (
    <Card withBorder>
      <Stack gap="md">
        <Stack gap={4}>
          <Text size="lg" fw={600}>
            <Trans>Floor plan</Trans>
          </Text>
          <Text size="sm" c="dimmed">
            <Trans>Tap a number to see what was photographed there.</Trans>
          </Text>
        </Stack>

        {plans.map(({ plan, spots }) => (
          <Stack key={plan.id} gap="xs">
            {plans.length > 1 && plan.description && (
              <Text fw={500}>{plan.description}</Text>
            )}
            <FloorPlanView
              src={plan.downloadUrl ?? ''}
              height="50vh"
              markers={spots.map((spot) => ({
                key: spot.number,
                x: spot.x,
                y: spot.y,
                label: spot.number,
              }))}
              onMarkerClick={(key) =>
                setShown(
                  spots.find((spot) => String(spot.number) === key)
                    ?.attachments,
                )
              }
            />
            {spots.map((spot) => (
              <UnstyledButton
                key={spot.number}
                onClick={() => setShown(spot.attachments)}
              >
                <Text size="sm">
                  <Text span fw={700}>
                    {spot.number}.
                  </Text>{' '}
                  {spotLabel(spot.attachments)}
                </Text>
              </UnstyledButton>
            ))}
          </Stack>
        ))}
      </Stack>

      <PhotoPreviewModal
        photos={shown ?? []}
        initialIndex={0}
        opened={!!shown}
        onClose={() => setShown(undefined)}
      />
    </Card>
  )
}
